// ── CAPR — A CONTROL'S AUDIT HISTORY (B-CAPR-CONTROL-AUDIT-TAB-HISTORY), read from audit_logs ────────
//
// ⚠ READ-ONLY, NEWEST FIRST, AS RECORDED. Each row is one audit event the server keyed to this control: a
//   review cadence change (from → to, and the rationale recorded with it), an assessment started (with its
//   reason), a decision (its outcome).
// ⚠ GAPS ARE NAMED, NOT PAPERED OVER. Submissions and reassignments are not recorded as audit events, and
//   some earlier starts were never recorded — the footnote says so, so an absent row cannot read as
//   "nothing happened". An event type this surface does not know is still listed, by its recorded name.
// ⚠ THE SERVER DECIDES WHO MAY READ IT (risk_manager / super_admin). For anyone else the history is not
//   requested at all, and the tab says who can see it rather than drawing a refusal.

import { CTS_REVIEW_CADENCE_LABEL, type CtsReviewCadence } from '@grc/shared';
import { useControlAuditHistory } from '@/api/hooks/useCtsWorkspace';
import type { ControlAuditEvent } from '@/api/types/cts-workspace.types';
import { Skeleton } from '@/components/ui/skeleton';
import { reasonLabel } from './assessment/AssessmentContext';

const NOTE = 'px-[16px] py-[18px] text-[13px] italic text-text-faint';

const cadenceText = (c: CtsReviewCadence | null): string =>
  c ? (CTS_REVIEW_CADENCE_LABEL[c] ?? c) : 'Organization default';

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

/** What happened, in the product's words; an unknown event keeps its recorded name. */
export function auditEventTitle(e: ControlAuditEvent): string {
  switch (e.event_type) {
    case 'cts_control.review_cadence_override_set': return 'Review cadence changed';
    case 'cts_control.review_cadence_override_cleared': return 'Review cadence returned to the organization default';
    case 'cts_assignment.started': return 'Assessment started';
    case 'cts_assessment.approved':
    case 'cts_assessment.returned':
      return e.outcome_kind === 'returned' ? 'Assessment returned' : 'Assessment approved';
    default: return e.event_type;
  }
}

/** The event's own facts: the cadence from → to, or the reason a start was made. */
export function auditEventDetail(e: ControlAuditEvent): string | null {
  if (e.event_type.startsWith('cts_control.review_cadence_override_')) {
    return `${cadenceText(e.cadence_from)} → ${cadenceText(e.cadence_to)}`;
  }
  if (e.event_type === 'cts_assignment.started') {
    const reason = reasonLabel(e.reason_started);
    return reason ? `Reason: ${reason}` : null;
  }
  return null;
}

export function ControlAuditHistory({ reference, canView }: { reference: string; canView: boolean }) {
  const { data, isLoading, isError } = useControlAuditHistory(reference, canView);

  if (!canView) {
    return <p className={NOTE}>A control&apos;s audit history is visible to risk managers and super admins.</p>;
  }
  if (isLoading) return <div className="p-[16px]"><Skeleton className="h-[56px] w-full" /></div>;
  if (isError || !data) return <p className={NOTE}>The audit history could not be loaded.</p>;

  return (
    <div data-testid="control-audit-history">
      {data.length === 0 ? (
        <p className={NOTE}>No audit events have been recorded for this control yet.</p>
      ) : (
        <ul>
          {data.map((e) => {
            const detail = auditEventDetail(e);
            return (
              <li key={e.id} className="border-b border-border-subtle px-[16px] py-[12px] last:border-b-0" data-testid="audit-event">
                <div className="flex flex-wrap items-baseline gap-[10px]">
                  <span className="text-[13px] font-medium text-text-primary">{auditEventTitle(e)}</span>
                  <span className="ml-auto text-[11px] text-text-secondary">{fmtDateTime(e.occurred_at)}</span>
                </div>
                <p className="mt-[3px] text-[11.5px] text-text-secondary">
                  By {e.actor_name ?? 'an unknown user'}{detail ? ` · ${detail}` : ''}
                </p>
                {e.cadence_rationale && (
                  <p className="mt-[4px] whitespace-pre-wrap text-[12px] text-text-primary" data-testid="audit-rationale">
                    {e.cadence_rationale}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="border-t border-border-subtle px-[16px] py-[9px] text-[11px] text-text-faint">
        Review cadence changes, assessment starts and assessment decisions, as the audit log recorded them.
        Submissions and reassignments are not part of this history, and some earlier assessment starts were not recorded.
      </p>
    </div>
  );
}
